import { cloneElement, SyntheticEvent } from 'react';

import NumberPicker from '~/src/common/components/NumberPicker/NumberPicker';
import { usePickerDebounced } from '~/src/common/hooks/usePickerDebounced';
import I18n from '~/src/common/services/I18n';

interface PickerChildrenProps {
  onClick?: (e?: SyntheticEvent) => void;
}

interface Props {
  cachedQuantity: number;
  availableQuantity: number;
  emptyComponent: React.ReactElement<PickerChildrenProps>;
  enabled?: boolean;
  packSize?: number;
  renderContent?: (props: { value: number }) => React.ReactNode;
  size?: 'small' | 'large';
  onQuantityChange: (quantity: number) => void;
  onDisabledClick: (quantity: number, increment: number) => void;
}

const NumberPickerDebounced = ({
  onQuantityChange,
  onDisabledClick,
  cachedQuantity,
  availableQuantity,
  emptyComponent,
  enabled = true,
  packSize = 1,
  renderContent,
  size,
}: Props) => {
  const [debouncedQuantity, setDebouncedQuantity] = usePickerDebounced({
    cachedQuantity,
    onUpdate: (count: number) => onQuantityChange(count),
  });

  const handleQuantityChange = (currentQuantity: number) => {
    if (enabled) setDebouncedQuantity(currentQuantity);
    else {
      onDisabledClick(currentQuantity, packSize);
    }
  };

  return debouncedQuantity === 0 ? (
    cloneElement(emptyComponent, {
      onClick: () => {
        handleQuantityChange(packSize);
      },
    })
  ) : (
    <NumberPicker
      value={debouncedQuantity}
      onChange={handleQuantityChange}
      minimumValue={0}
      maximumValue={availableQuantity}
      maximumValueReachedText={I18n.t('common.sold-out')}
      step={packSize}
      size={size}>
      {renderContent}
    </NumberPicker>
  );
};

export default NumberPickerDebounced;
